import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { QrCode } from "../ui/QrCode";

/** Re-mint a little before the two-minute token lapses, so the QR on screen always works. */
const REFRESH_LEAD_MS = 20 * 1000;

/**
 * "Control from your phone" — the desktop's way of handing the remote over.
 *
 * The gap this fills was real and self-inflicted: inverting the screen pairing gave the
 * TV the code, which was right, but it left an operator sitting at a desktop with no way
 * at all to get the controller into their hand. Scanning a screen's QR pairs that SCREEN;
 * it does not move the session.
 *
 * So this runs the other way, and consistently: pairing attaches an account-less TV to an
 * account, so the TV displays and the controller reads; a handoff moves the identity
 * itself, so the device that HAS it displays and the phone acquiring it reads. Whoever is
 * acquiring does the reading, both times.
 *
 * Desktop only. On a phone you are already the thing this produces.
 *
 * It is the desktop's whole column beside the library now — the Goon Controller rail it
 * used to sit under is gone (screens are driven from the remote alone) — so it sizes its
 * own column rather than being an item in someone else's list.
 */
export function UsePhoneCard() {
  const [token, setToken] = useState<string | null>(null);
  // Open by default: on the desktop this card IS the way to the remote, so the QR should
  // be there to scan without a click first. Minting a handoff token on load is the cost —
  // the effect below mints only while open, and re-mints before the 2-min token lapses.
  const [open, setOpen] = useState(true);
  const [error, setError] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const mint = useCallback(async () => {
    try {
      const r = await api.control.handoff();
      setToken(r.token);
      setError(false);
      // Re-armed from what the SERVER said rather than a local copy of the TTL: the two
      // would drift the moment one changed, and the failure is silent — a QR on screen
      // that stopped working a minute ago.
      const wait = Math.max(15_000, r.expiresAt - Date.now() - REFRESH_LEAD_MS);
      timer.current = setTimeout(() => void mint(), wait);
    } catch {
      setError(true);
    }
  }, []);

  // Only while the QR is actually showing. A token minted for a closed card is a live
  // credential nobody is looking at, and re-minting one on a timer forever is worse.
  useEffect(() => {
    if (!open) {
      if (timer.current) clearTimeout(timer.current);
      setToken(null);
      return;
    }
    void mint();
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [open, mint]);

  const url = token ? `${window.location.origin}/control/${token}` : "";

  return (
    /*
       Hidden on a COARSE pointer, not shown on a fine one. The distinction matters: a
       context that reports neither — headless Chrome does, and so does anything without a
       pointer media feature — would fail the `pointer-fine` test and hide this entirely.
       Gating on coarse makes visible the default and absence the exception, so the failure
       direction is "shown where it need not be" rather than "the only way to get the
       controller onto your phone silently disappears".
    */
    <aside className="w-full rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-3 pointer-coarse:hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <span className="text-xs font-semibold uppercase tracking-wide text-zinc-300">
          Control from your phone
        </span>
        <span className="text-zinc-500">{open ? "▴" : "▾"}</span>
      </button>

      {open ? (
        error ? (
          <p className="mt-2 text-[11px] text-zinc-500">Couldn&rsquo;t get a link — try again.</p>
        ) : url ? (
          <>
            {/* BLOCKED in session replay (`ph-no-capture`, posthog-js's default blockClass,
                as on display/PartyBadge): the QR encodes `/control/<token>`, a live handoff
                token that signs a phone in as this browser, and /views is a recorded route.
                MASK hides text, not an SVG path's `d`. The token and its URL appear nowhere
                else on the card — no link, no copy text, no title. */}
            <div className="ph-no-capture mt-2 rounded-lg bg-white p-2">
              <QrCode text={url} className="h-auto w-full" />
            </div>
            <p className="mt-2 text-[11px] leading-relaxed text-zinc-500">
              Scan it. Your phone becomes the controller, with the same views and screens.
            </p>
          </>
        ) : (
          <p className="mt-2 text-[11px] text-zinc-500">Getting a link…</p>
        )
      ) : (
        <p className="mt-1 text-[11px] leading-relaxed text-zinc-500">
          Scan a code and drive everything one-handed.
        </p>
      )}
    </aside>
  );
}
